import { apiClient } from "./client";

/**
 * Tipi e chiamate dell'invio dei documenti per email.
 *
 * Le funzioni dei DUE PDF che hanno un record loro (sopralluogo, rapportino)
 * stanno nei moduli dei rispettivi endpoint, perché le rotte sono una per
 * modulo — ognuna ha la sua autorizzazione lato server. Qui vivono i tipi
 * condivisi e le chiamate dei due REPORT, che invece hanno un endpoint proprio
 * (`/api/report/...`) perché aggregano più moduli e non appartengono a nessuno.
 */

export interface DestinatarioProposto {
  email: string;
  /** Già pronta da mostrare, es. `Sede — Burger King Castel Mella`. */
  etichetta: string;
  origine: "sede" | "cliente" | "referente" | "centro_assistenza";
}

export interface EsitoInvio {
  inviati: string[];
  falliti: { email: string; errore: string }[];
}

/**
 * Quanti destinatari accetta un singolo invio. Deve restare allineato a
 * `MAX_DESTINATARI` lato backend, che è quello che rifiuta davvero: qui serve
 * solo a non far comporre una richiesta che verrà respinta.
 */
export const MAX_DESTINATARI = 5;

/** Estremi del periodo in formato `yyyy-MM-dd`, entrambi facoltativi. */
export interface PeriodoReport {
  da?: string;
  a?: string;
}

type GenereReport = "cliente" | "device";

/**
 * Il nome del file del report, con la stessa normalizzazione che applica il
 * backend in `routes/report.ts`.
 *
 * È ripetuta qui perché serve prima della richiesta: il pannello annuncia
 * "verrà allegato X" mentre il documento non esiste ancora, e senza questa
 * funzione annuncerebbe un nome — quello con gli spazi e le virgole della
 * ragione sociale — diverso da quello che poi arriva davvero nella casella.
 * Le due copie possono divergere; a divergere sarebbe l'etichetta, non il
 * file, e chi riceve continuerebbe a vedere il nome deciso dal server.
 */
export function nomeFileReport(prefisso: string, identificativo: string): string {
  return `${prefisso}-${identificativo}.pdf`.replace(/[^a-z0-9.\-]/gi, "_");
}

export async function destinatariPropostiReport(
  genere: GenereReport,
  id: string
): Promise<DestinatarioProposto[]> {
  const { data } = await apiClient.get<{ destinatari: DestinatarioProposto[] }>(`/report/${genere}/${id}/destinatari`);
  return data.destinatari;
}

/**
 * Scarica il PDF del report. Passa da `apiClient` e non da un link diretto
 * perché serve il token di autenticazione: stesso motivo per cui funzionano
 * così anche gli export Excel e il PDF del rapportino.
 */
export async function scaricaReport(
  genere: GenereReport,
  id: string,
  nomeFile: string,
  periodo: PeriodoReport = {}
): Promise<void> {
  const response = await apiClient.get(`/report/${genere}/${id}`, {
    params: { da: periodo.da || undefined, a: periodo.a || undefined },
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  // Il nome lo passa il chiamante, come per il PDF del rapportino: da un blob
  // il `Content-Disposition` della risposta non viene letto dal browser, e
  // senza questo il file scaricato si chiamerebbe diversamente da quello
  // spedito per email un minuto dopo, dallo stesso pannello.
  a.download = nomeFile;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Manda il report agli indirizzi indicati, uno per destinatario.
 *
 * Il periodo viaggia nel body insieme ai destinatari, non nella query: il
 * documento spedito dev'essere quello che si aveva davanti premendo Invia.
 */
export async function inviaReport(
  genere: GenereReport,
  id: string,
  destinatari: string[],
  nota: string,
  periodo: PeriodoReport = {}
): Promise<EsitoInvio> {
  const { data } = await apiClient.post<EsitoInvio>(`/report/${genere}/${id}/invia`, {
    destinatari,
    nota,
    da: periodo.da || undefined,
    a: periodo.a || undefined,
  });
  return data;
}
